import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useContext, useState, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@skatteetaten/ds-buttons';
import { dsI18n } from '@skatteetaten/ds-core-utils';
import { Checkbox } from '@skatteetaten/ds-forms';
import { BriefcaseOffSVGpath, BriefcaseMultipleSVGpath, BriefcaseSVGpath } from '@skatteetaten/ds-icons';
import { Heading } from '@skatteetaten/ds-typography';
import { RolePickerContext } from '../RolePickerContext/index.esm.js';
import { RolePickerRow } from '../RolePickerRow/index.esm.js';
import '@skatteetaten/ds-navigation';
import '@skatteetaten/ds-status';
import './styles.css';


var styles = {
    "businessList": "RolePickerBusinessList-module_businessList__15fne",
    "checkboxGroup": "RolePickerBusinessList-module_checkboxGroup__cdE2w",
    "showAllButtonWrapper": "RolePickerBusinessList-module_showAllButtonWrapper__ZffHt",
    "subUnitsList": "RolePickerBusinessList-module_subUnitsList__bDv87",
    "subUnit": "RolePickerBusinessList-module_subUnit__DIX6O"
};

const MAX_INITIAL_ITEMS = 5;
const RolePickerBusinessList = ({ businesses, filterQuery, showInactiveBusinesses: externalShowInactiveBusinesses, showSubunits: externalShowSubUnits })=>{
    const { t } = useTranslation('ds_overlays', {
        i18n: dsI18n
    });
    const ctx = useContext(RolePickerContext);
    const [showAll, setShowAll] = useState(false);
    const [showInactiveBusinesses, setShowInactiveBusinesses] = useState(externalShowInactiveBusinesses);
    const [showSubUnits, setShowSubUnits] = useState(externalShowSubUnits);
    const navRef = useRef(null);
    const handleShowAll = ()=>{
        const visibleLinks = navRef.current?.querySelectorAll('a').length ?? 1;
        setShowAll(true);
        navRef.current?.querySelectorAll('a')[visibleLinks - 1].focus();
    };
    const handleEntityClicked = async (entity)=>{
        ctx?.setLoadingEntityId(entity.organizationNumber);
        ctx?.onEntitySelect?.(entity).then((res)=>{
            if (res?.error) {
                ctx?.setError({
                    entityId: entity.organizationNumber,
                    message: res.error
                });
            }
            ctx.setLoadingEntityId(undefined);
        });
    };
    const visibleItems = useMemo(()=>{
        // lager en dyp kopiering av business-listen for å unngå mutasjon
        let items = JSON.parse(JSON.stringify(businesses.list));
        items = !showInactiveBusinesses ? items.filter((p)=>!p.isDeleted) : items;
        if (filterQuery) {
            // Filteret sjekker om `filterQuery` er til stede i organisasjonens navn, enhetstype eller organisasjonsnummer.
            // Hvis `showSubUnits` er sann, sjekker det også om noen av underenhetenes navn inkluderer `filterQuery`.
            items = items.filter((business)=>{
                const mainMatch = (business.name + business.unitType).toLowerCase().includes(filterQuery.toLowerCase()) || business.organizationNumber.includes(filterQuery.toLowerCase()) || business.mainOrganizationNumber?.includes(filterQuery.toLowerCase());
                const subunitMatch = showSubUnits && business.subunits?.some((sub)=>(sub.name.toLowerCase() + sub.unitType.toLowerCase()).includes(filterQuery.toLowerCase()) && (showInactiveBusinesses || !sub.isDeleted));
                if (mainMatch) {
                    return true;
                }
                if (subunitMatch) {
                    const filteredSubunits = business.subunits?.filter((sub)=>(sub.name.toLowerCase() + sub.unitType.toLowerCase()).includes(filterQuery.toLowerCase()) && (showInactiveBusinesses || !sub.isDeleted));
                    business.subunits = filteredSubunits;
                    return true;
                }
                return false;
            });
        } else {
            items = showAll ? items : items.slice(0, MAX_INITIAL_ITEMS);
        }
        return items;
    }, [
        filterQuery,
        businesses.list,
        showAll,
        showInactiveBusinesses,
        showSubUnits
    ]);
    const hasInactiveItems = businesses.list.some((business)=>business.isDeleted || business.subunits?.some((sub)=>sub.isDeleted));
    const hasItemsWithSubOranizations = businesses.list.some((business)=>business.subunits);
    const getShowAllCount = useCallback(()=>{
        if (showSubUnits && showInactiveBusinesses) {
            return businesses.total;
        }
        if (showSubUnits) {
            return businesses.list.filter((item)=>!item.isDeleted).reduce((total, curr)=>{
                const subUnitsCount = curr.subunits?.filter((sub)=>!sub.isDeleted).length ?? 0;
                return total + 1 + subUnitsCount;
            }, 0);
        }
        if (showInactiveBusinesses) {
            return businesses.list.length;
        }
        return businesses.list.filter((item)=>!item.isDeleted).length;
    }, [
        businesses.list,
        businesses.total,
        showInactiveBusinesses,
        showSubUnits
    ]);
    const displayShowAllButton = !showAll && !filterQuery && businesses.list?.length > MAX_INITIAL_ITEMS;
    return /*#__PURE__*/ jsxs("div", {
        children: [
            /*#__PURE__*/ jsx(Heading, {
                as: 'h2',
                level: 3,
                id: 'businessesHeadingId',
                children: filterQuery ? `${visibleItems.length} ${t('rolepicker.BusinessHits')}` : t('rolepicker.BusinessesHeading')
            }),
            hasInactiveItems || hasItemsWithSubOranizations ? /*#__PURE__*/ jsxs("div", {
                className: styles.checkboxGroup,
                children: [
                    hasInactiveItems ? /*#__PURE__*/ jsx(Checkbox, {
                        checked: showInactiveBusinesses,
                        onChange: (e)=>setShowInactiveBusinesses(e.target.checked),
                        children: t('rolepicker.ShowInactiveBusinessesHeader')
                    }) : null,
                    hasItemsWithSubOranizations ? /*#__PURE__*/ jsx(Checkbox, {
                        checked: showSubUnits,
                        onChange: (e)=>setShowSubUnits(e.target.checked),
                        children: t('rolepicker.ShowSubBusinessesHeading')
                    }) : null
                ]
            }) : null,
            /*#__PURE__*/ jsx("nav", {
                ref: navRef,
                "aria-labelledby": 'businessesHeadingId',
                children: /*#__PURE__*/ jsx("ul", {
                    className: styles.businessList,
                    children: visibleItems?.map((item)=>{
                        if (showSubUnits && item.subunits) {
                            const subUnits = showInactiveBusinesses ? item.subunits : item.subunits.filter((sub)=>!sub.isDeleted);
                            return /*#__PURE__*/ jsxs("li", {
                                children: [
                                    /*#__PURE__*/ jsx(RolePickerRow, {
                                        id: item.organizationNumber,
                                        title: `${item.name} ${item.unitType} ${item.isDeleted ? `(${t('rolepicker.Deleted')})` : ''}`,
                                        description: /*#__PURE__*/ jsxs(Fragment, {
                                            children: [
                                                t('rolepicker.BusinessDescriptionPrefix'),
                                                ' ',
                                                item.organizationNumber,
                                                ' ',
                                                /*#__PURE__*/ jsxs("em", {
                                                    children: [
                                                        '(',
                                                        t('rolepicker.MainBusiness'),
                                                        ')'
                                                    ]
                                                })
                                            ]
                                        }),
                                        svgPath: item.isDeleted ? BriefcaseOffSVGpath : BriefcaseMultipleSVGpath,
                                        onClick: ()=>{
                                            handleEntityClicked(item);
                                        }
                                    }),
                                    /*#__PURE__*/ jsx("ul", {
                                        className: styles.subUnitsList,
                                        children: subUnits.map((sub)=>{
                                            return /*#__PURE__*/ jsx("li", {
                                                className: styles.subUnit,
                                                children: /*#__PURE__*/ jsx(RolePickerRow, {
                                                    id: sub.organizationNumber,
                                                    title: `${sub.name} ${sub.unitType} ${sub.isDeleted ? `(${t('rolepicker.Deleted')})` : ''}`,
                                                    titleAs: 'h4',
                                                    description: /*#__PURE__*/ jsxs(Fragment, {
                                                        children: [
                                                            t('rolepicker.BusinessDescriptionPrefix'),
                                                            ' ',
                                                            sub.organizationNumber,
                                                            ' ',
                                                            /*#__PURE__*/ jsxs("em", {
                                                                children: [
                                                                    '(',
                                                                    t('rolepicker.SubUnit'),
                                                                    ')'
                                                                ]
                                                            })
                                                        ]
                                                    }),
                                                    svgPath: sub.isDeleted ? BriefcaseOffSVGpath : BriefcaseSVGpath,
                                                    onClick: ()=>{
                                                        handleEntityClicked(sub);
                                                    }
                                                })
                                            }, sub.organizationNumber);
                                        })
                                    })
                                ]
                            }, item.organizationNumber);
                        } else {
                            const svgPath = item.subunits ? BriefcaseMultipleSVGpath : BriefcaseSVGpath;
                            return /*#__PURE__*/ jsx("li", {
                                children: /*#__PURE__*/ jsx(RolePickerRow, {
                                    id: item.organizationNumber,
                                    title: `${item.name} ${item.unitType} ${item.isDeleted ? `(${t('rolepicker.Deleted')})` : ''}`,
                                    description: `${t('rolepicker.BusinessDescriptionPrefix')} ${item.organizationNumber}`,
                                    svgPath: item.isDeleted ? BriefcaseOffSVGpath : svgPath,
                                    onClick: ()=>{
                                        handleEntityClicked(item);
                                    }
                                })
                            }, item.organizationNumber);
                        }
                    })
                })
            }),
            displayShowAllButton ? /*#__PURE__*/ jsx("div", {
                className: styles.showAllButtonWrapper,
                children: /*#__PURE__*/ jsx(Button, {
                    variant: 'tertiary',
                    onClick: handleShowAll,
                    children: `${t('rolepicker.ShowAll')} ${t('rolepicker.Businesses')} (${getShowAllCount()})`
                })
            }) : null
        ]
    });
};
RolePickerBusinessList.displayName = 'RolePickerBusinessList';

export { RolePickerBusinessList };
